import { useTranslation } from 'react-i18next';
import { DATE_FORMATS } from '@callmart/types';
import { getLocationDeltaByDateRangeLocationAndWorktype } from '../../services/locationDelta';
import { getFirstDayOfMonth, getLastDayOfMonth } from '../../utils/dateUtils';
import { usePreferredUserLocation, useChannels } from '../../hooks';
import DownloadExcelButton from '../Buttons/DownloadExcelButton';
import { getWorkingUnitShortName } from '../../utils/workingUnitUtils';
import dayjs, { formatSlotTime } from '@utils/dayjs';

const columnNames = [
    'START_TIME',
    'HOUR',
    'MINUTE',
    'Calldelta',
    'Stadt',
    'Standort Detail',
    'Worktype',
    'Kanal',
    'Grund',
];

function LocationDeltaCurrentDataDownloadButton({ date, selectedLocation, worktypes }) {
    const { t } = useTranslation('allDeltas');
    const [location] = usePreferredUserLocation(selectedLocation);
    const [channels] = useChannels();

    const mapDeltaToRow = (delta, worktypeName) => {
        const start_time = formatSlotTime(delta.slotDate, DATE_FORMATS.DATE);
        const hour = parseInt(formatSlotTime(delta.slotDate, 'HH'));
        const minute = parseInt(formatSlotTime(delta.slotDate, 'mm'));
        const locationDeltaValue = delta.locationDeltaValue;
        const channel = delta.channelName || '';
        const reason = delta.reason || '';
        return [
            start_time,
            hour,
            minute,
            locationDeltaValue,
            location?.city,
            location?.code,
            worktypeName,
            channel,
            reason,
        ];
    };

    const getXLSXSheets = async (worktypes) => {
        const sheetsData = [];

        const channelShortNames = {};
        for (const ch of channels) {
            channelShortNames[ch.name] = ch.shortName || ch.name;
        }

        for (const worktype of worktypes) {
            const firstDayOfMonth = getFirstDayOfMonth(date);
            const lastDayOfMonth = getLastDayOfMonth(date);
            const locationDeltas = await getLocationDeltaByDateRangeLocationAndWorktype(
                firstDayOfMonth,
                lastDayOfMonth,
                location?.id,
                worktype.id
            );

            const worktypeSheetName = worktype.shortName || worktype.worktype;

            const distinctChannelNames = [...new Set(locationDeltas.map((d) => d.channelName))];

            for (const channelName of distinctChannelNames) {
                const channelData = locationDeltas
                    .filter((delta) => delta.channelName === channelName)
                    .map((delta) => mapDeltaToRow(delta, worktype.worktype));

                const sheetName = channelName
                    ? getWorkingUnitShortName({
                          worktype: { shortName: worktype.shortName, worktype: worktype.worktype },
                          channel: { shortName: channelShortNames[channelName], name: channelName },
                      })
                    : worktypeSheetName;

                sheetsData.push({
                    sheetName,
                    sheetData: channelData,
                });
            }
        }

        return sheetsData;
    };

    return (
        <DownloadExcelButton
            title="Standortdelta Template"
            fileName={`delta_${location?.city}_${dayjs(date).format(DATE_FORMATS.MONTH_YEAR_UNDERSCORE)}.xlsx`}
            location={selectedLocation}
            columnNames={columnNames}
            getSheetsData={() => getXLSXSheets(worktypes)}
            buttonText={t('template.currentButton')}
        ></DownloadExcelButton>
    );
}

export default LocationDeltaCurrentDataDownloadButton;
